import { createContext, memo, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  EdgeProps as FlowEdgeProps,
  ReactFlowInstance,
  ReactFlowProps,
  useEdgesState,
  useNodesState,
  useStoreApi,
} from 'reactflow';

import { mapValues } from 'lodash-es';
import 'reactflow/dist/style.css';
import styled from 'styled-components';

import { useLocalStorageState } from '@components/component-helpers';
import { hideSidebar } from '@components/sidebar';
import { theme } from '@styles/theme';
import { os } from '@utils/operating-system';

import { JourneyForm } from 'api/journey-forms/requests';
import { PollingConfigList } from 'api/journey-polling/requests';
import { JourneyWebhookList } from 'api/journey-webhooks/requests';
import { useFeature } from 'ui/hooks';

import { AI_BUILDER_VIEWPORT_SHIFT, fitViewWithOffset } from './ai-builder/ai-builder-layout';
import { Controls } from './controls';
import { useAnimatedNodes } from './hooks/use-animated-nodes';

export type EdgeProps = FlowEdgeProps;

export type BaseBuilderProps = Pick<ReactFlowProps, 'edgeTypes'> & {
  nodeTypes: { [key in string]: { Node: ReactNode } };
  className?: string;
  isDragging?: boolean;
  journeyId?: string;
  isTemplate?: boolean;
  isPreviewMode?: boolean;
};

export const zoomDuration = 250;

export type journeyActionFormContextMapType = Record<
  string,
  { shortId: string; name: string; formUrl?: string }
>;

type JourneyContextType = {
  journeyActionForms: journeyActionFormContextMapType;
  journeyForms: JourneyForm[];
  webhooks: JourneyWebhookList[];
  pollingConfigs: PollingConfigList[];
  triggersCount: number;
  journeyId?: string;
};

export const JourneyContext = createContext<JourneyContextType | undefined>(undefined);

export const useJourneyContext = () => {
  const context = useContext(JourneyContext);
  if (!context) {
    throw new Error('useJourneyContext must be used within a JourneyContextProvider');
  }
  return context;
};

export const BaseBuilder = memo(
  ({ nodeTypes, isDragging, journeyId, isTemplate, isPreviewMode, ...rest }: BaseBuilderProps) => {
    const [nodes, , onNodesChange] = useNodesState([]);
    const [edges, , onEdgesChange] = useEdgesState([]);
    const { animatedNodes } = useAnimatedNodes(nodes);
    const [isPanningKey, setIsPanningKey] = useState(false);
    const reactFlowWrapper = useRef<HTMLDivElement>(null);
    const reactFlowInstance = useRef<ReactFlowInstance | null>(null);
    const store = useStoreApi();
    const { isEnabled: hasAiBuilderFeature } = useFeature('FEATURE_AI_BUILDER');
    const [hasAiBuilderOpen, setHasAiBuilderOpen] = useLocalStorageState({
      key: 'has-ai-builder-open',
      defaultValue: true,
    });
    const isAiBuilderOpen =
      hasAiBuilderFeature && hasAiBuilderOpen && !isTemplate && Boolean(journeyId);
    const viewportOffsetX = isAiBuilderOpen ? AI_BUILDER_VIEWPORT_SHIFT : 0;

    const nodeComponents = useRef(mapValues(nodeTypes, (value) => value.Node));

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        const key = os === 'MacOS' ? event.metaKey : event.ctrlKey;
        if (event.key === ' ') {
          setIsPanningKey(true);
        }
        if (key && (event.key === '=' || event.key === '+')) {
          event.preventDefault();
          reactFlowInstance.current?.zoomIn({ duration: zoomDuration });
        }
        if (key && event.key === '-') {
          event.preventDefault();
          reactFlowInstance.current?.zoomOut({ duration: zoomDuration });
        }
        if (key && (event.key === '0' || event.key === 'Home')) {
          event.preventDefault();
          const instance = reactFlowInstance.current;
          if (!instance?.viewportInitialized) return;
          const { width, height, minZoom } = store.getState();
          fitViewWithOffset({
            nodes: instance.getNodes(),
            viewportWidth: width,
            viewportHeight: height,
            minZoom,
            maxZoom: 1,
            offsetX: viewportOffsetX,
            duration: zoomDuration,
            setViewport: instance.setViewport,
          });
        }
      };

      const handleKeyUp = (event: KeyboardEvent) => {
        if (event.key === ' ') {
          setIsPanningKey(false);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
      };
    }, [store, viewportOffsetX]);

    return (
      <StyledReactFlow
        ref={reactFlowWrapper}
        onInit={(instance) => (reactFlowInstance.current = instance)}
        deleteKeyCode={null}
        nodes={animatedNodes}
        // TS complaining that we use JourneyNode instead of NodeProps from reactflow as parameter type
        // for our nodes components
        // @ts-ignore-next-line
        nodeTypes={nodeComponents.current}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodesDraggable={false}
        nodesConnectable={false}
        zoomActivationKeyCode={['Control', 'Meta']}
        multiSelectionKeyCode={null}
        selectionKeyCode={null}
        fitView
        fitViewOptions={{ maxZoom: 1 }}
        proOptions={{ hideAttribution: true }}
        style={{
          background: theme.color.uiTrans5,
          ...(isPreviewMode && {
            background: `
                repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 14px,
                  ${theme.color.uiTrans10} 14px,
                  ${theme.color.uiTrans10} 16px
                )
              `,
          }),
        }}
        maxZoom={2}
        minZoom={0.3}
        zoomOnScroll={false}
        panOnDrag={[1, 2]}
        panOnScroll
        $isPanningKey={isPanningKey}
        defaultEdgeOptions={{
          type: 'defaultEdge',
        }}
        onPaneClick={hideSidebar}
        {...rest}
      >
        {!isPreviewMode && <Background />}
        {nodes.length !== 0 && (
          <Controls
            journeyId={journeyId}
            isTemplate={isTemplate}
            canSeeAiBuilder={hasAiBuilderFeature}
            isAiBuilderOpen={isAiBuilderOpen}
            setHasAiBuilderOpen={setHasAiBuilderOpen}
          />
        )}
      </StyledReactFlow>
    );
  }
);

const StyledReactFlow = styled(ReactFlow)<{ $isPanningKey: boolean }>(
  ({ $isPanningKey = false }) => ({
    '.react-flow__pane': {
      cursor: $isPanningKey ? 'grab' : 'default',
    },

    '.react-flow__pane.dragging': {
      cursor: 'grabbing',
    },
  })
);
